import { CATALOG_PRODUCTS } from "../data/products";
import { SEO_FAQ } from "./faq";
import {
  BUSINESS,
  LOGO_PATH,
  PRIMARY_KEYWORDS,
  SITE_NAME,
  SITE_SHORT_NAME,
  SITE_TAGLINE,
  SITE_URL,
  toAbsoluteUrl,
  toProductSlug,
} from "./constants";

const productCatalogItems = CATALOG_PRODUCTS.map((product, index) => ({
  "@type": "ListItem",
  position: index + 1,
  name: product.name,
  description: product.description,
  image: toAbsoluteUrl(product.image),
  url: `${SITE_URL}/products#${toProductSlug(product.name)}`,
}));

export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Manufacturer",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: [
    SITE_SHORT_NAME,
    "Rahul Engineering Works Kanpur",
    "Rahul Engineering Work Kanpur",
    "REW Bakery Machines Kanpur",
  ],
  url: SITE_URL,
  logo: toAbsoluteUrl(LOGO_PATH),
  image: toAbsoluteUrl("/hero.webp"),
  description:
    "Rahul Engineering Works Kanpur manufactures bakery machines, industrial ovens, dough mixers and bread slicers for commercial bakeries across India since 1992.",
  email: BUSINESS.email,
  telephone: BUSINESS.phones,
  taxID: BUSINESS.gst,
  foundingDate: String(BUSINESS.foundingYear),
  address: {
    "@type": "PostalAddress",
    ...BUSINESS.address,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: BUSINESS.geo.latitude,
    longitude: BUSINESS.geo.longitude,
  },
  areaServed: [
    { "@type": "City", name: "Kanpur" },
    { "@type": "State", name: "Uttar Pradesh" },
    { "@type": "Country", name: "India" },
  ],
  sameAs: Object.values(BUSINESS.social),
  keywords: PRIMARY_KEYWORDS.join(", "),
  knowsAbout: PRIMARY_KEYWORDS,
});

export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: [
    SITE_SHORT_NAME,
    "Rahul Engineering Works Kanpur",
    "Bakery Machines Kanpur",
  ],
  url: SITE_URL,
  description: SITE_TAGLINE,
  inLanguage: "en-IN",
  publisher: { "@id": `${SITE_URL}/#organization` },
  about: PRIMARY_KEYWORDS.map((keyword) => ({
    "@type": "Thing",
    name: keyword,
  })),
});

export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Store"],
  "@id": `${SITE_URL}/#localbusiness`,
  name: `${SITE_NAME} — Kanpur`,
  alternateName: "Rahul Engineering Works Kanpur",
  image: toAbsoluteUrl("/hero.webp"),
  url: SITE_URL,
  telephone: BUSINESS.phones,
  email: BUSINESS.email,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    ...BUSINESS.address,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: BUSINESS.geo.latitude,
    longitude: BUSINESS.geo.longitude,
  },
  hasMap: "https://maps.google.com/maps?q=26.449514,80.271431",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    opens: "09:00",
    closes: "18:00",
  },
});

export const getFaqSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: SEO_FAQ.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const getProductSchemas = () =>
  CATALOG_PRODUCTS.map((product) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: toAbsoluteUrl(product.image),
    category: product.category,
    brand: {
      "@type": "Brand",
      name: SITE_NAME,
    },
    manufacturer: { "@id": `${SITE_URL}/#organization` },
    url: `${SITE_URL}/products#${toProductSlug(product.name)}`,
    areaServed: "India",
  }));

export const getBreadcrumbSchema = (pathname) => {
  const crumbs = [{ name: "Home", path: "/" }];

  if (pathname === "/products") {
    crumbs.push({ name: "Bakery Machines & Products", path: "/products" });
  } else if (pathname === "/clients") {
    crumbs.push({ name: "Clients", path: "/clients" });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: toAbsoluteUrl(crumb.path),
    })),
  };
};

export const getProductListSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Bakery Machines, Ovens, Mixers & Slicers — Rahul Engineering Works",
  description:
    "Industrial bakery equipment catalog: ovens, mixers, slicers and production lines from Kanpur, India.",
  numberOfItems: CATALOG_PRODUCTS.length,
  itemListElement: productCatalogItems,
});

export const getStructuredDataForPath = (pathname) => {
  const graph = [
    getOrganizationSchema(),
    getWebSiteSchema(),
    getLocalBusinessSchema(),
    getBreadcrumbSchema(pathname),
    getFaqSchema(),
  ];

  if (pathname === "/") {
    graph.push(getProductListSchema());
  }

  if (pathname === "/products") {
    graph.push(getProductListSchema(), ...getProductSchemas());
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
};
